import React from 'react';
import type { AnyLevel } from '../../../data/schemas';
import { isSoppelLevel } from '../../../data/loadData';
import { RewardScreen } from '../rewardScreen/RewardScreen';
import { shouldMountLevelWorld } from './shouldMountLevelWorld';
import { useLevelRunner } from './useLevelRunner';

export interface LevelRunnerShellProps {
  level: AnyLevel;
  gameId: string;
  children?: (api: ReturnType<typeof useLevelRunner>) => React.ReactNode;
  /** Leave the level (e.g. after the reward): back to the level list. */
  onExit: () => void;
  /** Home icon in the corner: back to the home screen. Defaults to `onExit`. */
  onHome?: () => void;
}

/** House icon in the corner: one tap goes back to the home screen. */
const HomeButton: React.FC<{ onHome: () => void }> = ({ onHome }) => (
  <button
    type="button"
    data-name="home-button"
    aria-label="Hjem"
    className="absolute top-4 left-4 z-30 select-none touch-none rounded-2xl bg-white/95 text-4xl shadow active:scale-95"
    style={{ minWidth: 96, minHeight: 96 }}
    onClick={onHome}
  >
    <span aria-hidden>🏠</span>
  </button>
);

/**
 * Shared level shell: start → play → finish → celebration → reward.
 */
export const LevelRunnerShell: React.FC<LevelRunnerShellProps> = ({
  level,
  gameId,
  children,
  onExit,
  onHome,
}) => {
  const api = useLevelRunner(level);
  const hasCustomPlay = Boolean(children);
  const soppelInWorldStart = hasCustomPlay && isSoppelLevel(level);
  const showWorld = shouldMountLevelWorld(api.phase, hasCustomPlay);
  const hideWorldUnderReward = hasCustomPlay && api.phase === 'reward';

  return (
    <div
      className="relative h-[100dvh] min-h-screen w-full overflow-hidden bg-gradient-to-b from-[#8ecae6] to-[#669bbc] select-none touch-none"
      data-name={`level-${level.id}`}
      data-game={gameId}
      data-phase={api.phase}
    >
      <HomeButton onHome={onHome ?? onExit} />

      {/* Non-Søppel / demo: classic start card with Start button */}
      {api.phase === 'start' && !soppelInWorldStart ? (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 p-6">
          <p className="text-4xl font-display font-bold text-white drop-shadow text-center">
            {level.title}
          </p>
          <p
            className="max-w-md text-center font-body text-xl text-white/95 drop-shadow"
            data-name="intro-voice-echo"
          >
            {level.voice.intro}
          </p>
          <div className="flex items-center gap-3 text-5xl" aria-hidden>
            <span>🚛</span>
            <span className="animate-bounce">👆</span>
            <span>🗑️</span>
          </div>
          <button
            type="button"
            onClick={api.startPlay}
            className="min-h-[96px] min-w-[200px] rounded-2xl bg-[#F9B233] px-8 font-display text-2xl font-bold text-white shadow-lg active:scale-95"
          >
            Start
          </button>
        </div>
      ) : null}

      {showWorld ? (
        <div
          className={
            hasCustomPlay
              ? 'absolute inset-0 z-10 h-full min-h-[100dvh] w-full'
              : 'relative min-h-screen pt-28 pb-10 px-4'
          }
          // Keep R3F mounted under reward; hide so RewardScreen (z-40) owns the view.
          style={
            hideWorldUnderReward
              ? { visibility: 'hidden', pointerEvents: 'none' }
              : undefined
          }
          aria-hidden={hideWorldUnderReward || undefined}
          data-world-mounted="true"
        >
          {children ? children(api) : <Phase0PlayDemo level={level} api={api} />}
          {!hasCustomPlay && api.idleHintVisible ? (
            <div
              className="pointer-events-none absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce text-6xl"
              data-name="idle-hint-hand"
              aria-hidden
            >
              👆
            </div>
          ) : null}
        </div>
      ) : null}

      {api.phase === 'reward' ? (
        <RewardScreen
          reward={level.reward}
          levelTitle={level.title}
          onContinue={() => {
            api.dismissReward();
            onExit();
          }}
        />
      ) : null}
    </div>
  );
};

const Phase0PlayDemo: React.FC<{
  level: AnyLevel;
  api: ReturnType<typeof useLevelRunner>;
}> = ({ level, api }) => {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-6 text-center">
      <p className="text-white/90 font-body text-lg">
        Demo — trykk den store knappen for å fullføre nivået.
      </p>
      <button
        type="button"
        data-name={`demo-complete-${level.id}`}
        className="min-h-[96px] min-w-[96px] rounded-full bg-white px-8 font-display text-2xl font-bold text-[#27A9E1] shadow-lg active:scale-95"
        onClick={() => {
          api.pokeActivity();
          api.completeLevel();
        }}
      >
        Ferdig!
      </button>
    </div>
  );
};

export default LevelRunnerShell;
